﻿import * as layerTypes from '../utils/layerTypes';
import * as paths from '../utils/paths';
import * as eventTypes from '../utils/eventTypes';
import { OSM, Vector as VectorSource, WMTS } from 'ol/source';
import { Image as ImageLayer, Vector as VectorLayer, WebGLVector as WebGLLayer } from 'ol/layer';
import TileLayer from 'ol/layer/Tile';
import { addOption, getResolutionForScale } from '../utils/utils';
import * as formats from '../utils/formats';
import * as defaults from '../utils/defaults';
import ImageWMS from 'ol/source/ImageWMS';
import XGeoJSON from '../format/XGeoJSON';
import { handleGeoserviceWfsUrl, handleGeoserviceGeojsonWfsUrl, handleNativeUrl, handleWfsUrl, handleAnalysisLoader, handleGeoserverVectorTileLayerUrl } from '../utils/utils';
import { xhr } from '../loader/xfeatureloader.js';
import { bbox as bboxStrategy, all as allStrategy } from 'ol/loadingstrategy';
import StyleHandler from './StyleHandler';
import * as styleTypes from '../utils/style/styleTypes';
import { createOlStyleFunction } from '../utils/style/canvas/OlStyler';
import * as geomTypes from '../utils/geomTypes';
import WKT from 'ol/format/WKT';
import { transformExtent } from 'ol/proj';
import Heatmap from 'ol/layer/Heatmap';
import VectorImageLayer from 'ol/layer/VectorImage.js';
import ImageTile from 'ol/source/ImageTile.js';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import { get as getProjection } from 'ol/proj';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import TileGrid from 'ol/tilegrid/TileGrid';
import { getTopLeft, getHeight } from 'ol/extent';
import XMVT from '../format/XMVT';
import XTopoJSON from '../format/XTopoJSON';

export default class LayerHandler {
    constructor(mapId, map, layerSettingsHandler, projectionHandler, generalSettingsHandler, gisSettingsHandler, selectionHandler, rootURL, classId, mapControl) {
        this.mapControl = mapControl;
        this.rootURL = rootURL;
        this.map = map;
        this.mapId = mapId;
        this.layerSettingsHandler = layerSettingsHandler;
        this.projectionHandler = projectionHandler;
        this.generalSettingsHandler = generalSettingsHandler;
        this.gisSettingsHandler = gisSettingsHandler;
        this.selectionHandler = selectionHandler;
        this.styleHandler = new StyleHandler(this.rootURL);
        this.selectionHandler.styleHandler = this.styleHandler;
        this.selectionHandler.layerHandler = this;
        this.layerIdx = 0;
        this.layerIdxTemp = 0;
        this.layers = [];
        this.temporaryLayerIdx = 5000;
        this.classId = classId;
        this.tileLoadingEnabled = true;
        this.tileSources = [];
        this.refreshIds = {};
        this._registerEventHandlers();
    }

    refreshLayer(layerId, force) {
        try {
            var layer = this.getLayerById(layerId)[0];
            if (layer.getSource().strategy_ == allStrategy && !force) {
                return;
            }


            if (layer != null && layer.getSource() != null) {
                try {
                    layer.getSource().clear();
                } catch (e) {

                }
                layer.getSource().refresh();
            }
        }
        catch (e) {
        }
    }

    enableTile() {
        this.tileLoadingEnabled = true;
        this.tileSources.forEach(source => {
            source.refresh();
        });
    }

    disableTile() {
        this.tileLoadingEnabled = false;
    }

    init() {
        return new Promise((resolve, reject) => {
            let stylesMap = {};
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_GEOLOCATION] = 'sys_' + styleTypes.SYSTEM_STYLE_GEOLOCATION;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_POINT] = 'sys_' + styleTypes.SYSTEM_STYLE_POINT;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_LINE] = 'sys_' + styleTypes.SYSTEM_STYLE_LINE;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_POLYGON] = 'sys_' + styleTypes.SYSTEM_STYLE_POLYGON;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_ALL] = 'sys_' + styleTypes.SYSTEM_STYLE_ALL;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_EDIT_POINT] = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_POINT;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_EDIT_LINE] = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_LINE;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_EDIT_POLYGON] = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_POLYGON;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_EDIT_ALL] = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_ALL;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_DWG] = 'sys_' + styleTypes.SYSTEM_STYLE_DWG;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_SHAPE] = 'sys_' + styleTypes.SYSTEM_STYLE_SHAPE;
            this._reduceStyles();

            let styles = Object.keys(stylesMap);
            let stylePromises = [];

            styles.forEach(style => {
                stylePromises.push(this.styleHandler.loadStyle(style));
            });

            Promise.all(stylePromises).then(() => {
                let layerPromises = [];
                this._createLayers(this.layerSettingsHandler.layers, layerPromises);
                Promise.all(layerPromises).then(() => { resolve(); });
            });
        });
    }

    _reduceStyles() {
        if (this.layerSettingsHandler.styles != null) {
            let styleIds = Object.keys(this.layerSettingsHandler.styles);

            styleIds.forEach(styleId => {
                if (this.layerSettingsHandler.styles[styleId] != null && this.layerSettingsHandler.styles[styleId].trim() != '') {
                    try {
                        let style = JSON.parse(this.layerSettingsHandler.styles[styleId]);
                        this.styleHandler.applyStyle(styleId, style);
                    } catch (err) {

                    }
                }
            });
        }
    }

    _reduceGeoserviceStyle(layer, styles) {
        if (layer.Geoservice.ServiceType == layerTypes.WFS) {
            let defaultStyle = layer.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layer.IdDefaultStyle;

            if (defaultStyle != null && styles[defaultStyle] == null) {
                styles[defaultStyle] = defaultStyle;
            }

            if (layer.IdHighlightStyle != null && styles[layer.IdHighlightStyle] == null) {
                styles[layer.IdHighlightStyle] = layer.IdHighlightStyle;
            }

            if (layer.IdSelectStyle != null && styles[layer.IdSelectStyle] == null) {
                styles[layer.IdSelectStyle] = layer.IdSelectStyle;
            }
        }
    }

    _reduceEntityStyle(layer, styles) {
        if (layer.Interpretation == layerTypes.XP_NATIVE || layer.Interpretation == layerTypes.WFS) {
            let defaultStyle = layer.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layer.IdDefaultStyle;

            if (defaultStyle != null && styles[defaultStyle] == null) {
                styles[defaultStyle] = defaultStyle;
            }

            if (layer.IdHighlightStyle != null && styles[layer.IdHighlightStyle] == null) {
                styles[layer.IdHighlightStyle] = layer.IdHighlightStyle;
            }

            if (layer.IdSelectStyle != null && styles[layer.IdSelectStyle] == null) {
                styles[layer.IdSelectStyle] = layer.IdSelectStyle;
            }
        }
    }

    refresh() {
        for (var i = 0; i < this.layers.length; i++) {
            if (this.layers[i].getSource().strategy_ != allStrategy) {
                try {
                    this.layers[i].getSource().clear();
                } catch (err) {

                }
                this.layers[i].getSource().refresh();
            }
        }
    }

    getEntityLayersDescriptors() {
        let map = {};

        this.layers.forEach(mapLayer => {
            let layer = this.layerSettingsHandler.getLayerSettings(mapLayer.get('uid'));

            if (layer.Type == layerTypes.ENTITY && (layer.Interpretation == layerTypes.WFS || layer.Interpretation == layerTypes.VECTOR_TILE || layer.Interpretation == layerTypes.NATIVE_XP)) {
                map[layer.Id] = {
                    Id: layer.Id,
                    Name: layer.Title,
                    Type: layer.Entity.Type,
                    IdRef: layer.Entity.Id
                }
            }
        });

        let keys = Object.keys(map);
        let result = [];
        keys.forEach(key => {
            result.push(map[key]);
        });

        return result;
    }

    handleProjectionChange(initialProjection, targetProjection) {
        this.layers.forEach(layer => {
            let layerId = layer.get("uid");
            let layerSettings = this.layerSettingsHandler.getLayerSettings(layerId);
            let xMin, yMin, xMax, yMax;

            if (layerSettings != null) {
                xMin = layerSettings.XMin;
                yMin = layerSettings.YMin;
                xMax = layerSettings.XMax;
                yMax = layerSettings.YMax;
            }

            let extent;

            if (xMin != null && yMin != null && xMax != null && yMax != null) {
                extent = transformExtent([xMin, yMin, xMax, yMax], initialProjection, targetProjection);
                this.layerSettingsHandler.setLayerExtent(layerId, extent);
            } else {
                extent = this.projectionHandler.getProjExtent(this.generalSettingsHandler.settings.ProjCode);
            }

            layer.setExtent(extent);

            if (layerId == layerTypes.SNAP || layerId == layerTypes.MEASUREMENTS || layerId == layerTypes.REDLINE || layerId == layerTypes.DIMENSIONING) {
                layer.getSource().getFeatures().forEach(feature => {
                    feature.getGeometry().transform(initialProjection, targetProjection);
                });
            }

            this.refreshLayer(layerId);
        });
    }

    getLayerStyle(layerId) {
        let layer = this.getLayerSettings(layerId);

        return this.styleHandler.getStyleById(layer.IdDefaultStyle);
    }

    getLayerByName(name) {
        for (var i = 0; i < this.layers.length; i++) {
            if (this.layers[i].get("name") == name) {
                return this.layers[i];
            }
        }
    }

    getLayerById(id) {
        let result = [];
        for (var i = 0; i < this.layers.length; i++) {
            if (this.layers[i].get("uid") == id) {
                result.push(this.layers[i]);
            }
        }
        return result;
    }

    changeLayerOpacity(layerId, opacity) {
        let layers = this.getLayerById(layerId);

        layers.forEach(layer => {
            layer.setOpacity(opacity);
        });
    }

    changeLayerVisibility(layerId, visible) {
        let layers = this.getLayerById(layerId);

        layers.forEach(layer => {
            layer.setVisible(visible);
        });
    }

    changeLayerClassificationVisibility(layerId, classificationIdx, visible) {
        let layers = this.getLayerById(layerId);

        layers.forEach(layer => {
            this._handleLayerClassificationVisibility(layer, layerId, classificationIdx, visible);
        });
    }

    _handleLayerClassificationVisibility(layer, layerId, classificationIdx, visible) {
        let isFirst = false;
        if (layer.get('currentStyle') == null) {
            let layerSettings = this.layerSettingsHandler.getLayerSettings(layerId);
            let style = this.styleHandler.getCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
            layer.set('currentStyle', style);
            isFirst = true;
        }

        let currentStyle = layer.get('currentStyle');

        for (let i = 0; i < currentStyle.style.rules.length; i++) {
            let rule = currentStyle.style.rules[i];

            if (rule.classificationIdx == classificationIdx) {
                if (visible) {
                    let temp = [];

                    if (rule.filter.predicates) {
                        rule.filter.predicates.forEach(predicate => {
                            if (!(predicate.propertyname == 'xstate' && predicate.literal == 4)) {
                                temp.push(predicate);
                            }
                        });
                        rule.filter.predicates = temp;
                    } else {
                        if (rule.filter.propertyname == 'xstate' && rule.filter.oldLiteral != null) {
                            rule.filter.literal = rule.filter.oldLiteral;
                        }
                    }
                } else {
                    if (rule.filter.predicates) {
                        rule.filter.predicates.push({
                            "type": "comparison",
                            "matchcase": true,
                            "operator": "propertyisequalto",
                            "propertyname": "xstate",
                            "literal": 4
                        });
                    } else {
                        if (rule.filter.propertyname == 'xstate') {
                            rule.filter.oldLiteral = rule.filter.literal;
                            rule.filter.literal = 4;
                        }
                    }
                }
            } else if (isFirst && visible) {
                if (rule.filter.predicates) {
                    rule.filter.predicates.push({
                        "type": "comparison",
                        "matchcase": true,
                        "operator": "propertyisequalto",
                        "propertyname": "xstate",
                        "literal": 4
                    });
                } else {
                    if (rule.filter.propertyname == 'xstate') {
                        rule.filter.oldLiteral = rule.filter.literal;
                        rule.filter.literal = 4;
                    }
                }
            }
        }


        layer.set('currentStyle', currentStyle);

        let styleFunction = (feature) => {
            let styleGetter = createOlStyleFunction(currentStyle.style, this.map);
            return styleGetter(feature);
        }
        layer.setStyle(styleFunction)
    }

    changeLayersOrder(layers, fromStart) {
        if (fromStart) {
            this.layerIdxTemp = 0;
        }

        for (let i = layers.length - 1; i >= 0; i--) {
            let layer = layers[i];

            if (layer.Type == layerTypes.GROUP) {
                this.changeLayersOrder(layer.Items);
            } else {
                let mapLayers = this.getLayerById(layer.Id);

                if (mapLayers != null && mapLayers.length > 0) {
                    mapLayers.forEach(mapLayer => {
                        mapLayer.setZIndex(this.layerIdxTemp);
                    })
                    this.layerIdxTemp++;
                }
            }
        }
    }

    removeLayers(ids, changeOrder) {
        if (ids != null && ids.length > 0) {
            for (let i = 0; i < ids.length; i++) {
                let layerId = ids[i];
                let idx = 0;
                let found = false;

                for (let j = 0; j < this.layers.length; j++) {
                    if (this.layers[j].get("uid") == layerId) {
                        found = true;
                        j = this.layers.length;
                    } else {
                        idx++;
                    }
                }

                if (found) {
                    let toRemove = this.layers[idx];
                    this.map.removeLayer(toRemove);
                    this.layers.splice(idx, 1);
                }
            }
        }

        if (changeOrder) {
            this.changeLayersOrder(this.layerSettingsHandler.layers, true);
        }
    }

    addTemporaryVectorLayer(options) {
        let optionsCheck = this._checkTemporaryVector(options);

        if (!optionsCheck.status) {
            return new Promise((resolve, reject) => { resolve(optionsCheck); });
        }

        return this._createTemporaryLayer(options);
    }

    clearSelection() {
        this.selectionHandler.clearSelection();
    }

    fitFeature(layerId, featureId) {
        let layer = this.getLayerById(layerId);

        let idField = this.getLayerIdField(layerId);
        if (layer.length > 0) {
            if (layer[0].isVisible()) {
                let feature = layer[0].getSource().getFeatures().find(x => x.get(idField) == featureId);
                return this._fitFeature(feature, layer[0]);
            } else {
                // TO DO de tradus
                swal.fire({ title: 'Atenție!', html: 'Stratul nu este vizibil în hartă.' })
            }
        }

        return false;
    }

    fitWktGeometry(wkt, scale, featureProjection, dataProjection) {
        let feature = this.projectionHandler.wktToGeometry(wkt, featureProjection, dataProjection);

        let resolution = getResolutionForScale(scale, this.generalSettingsHandler.settings.ProjCode);

        let extent = feature.getGeometry().getExtent();

        var _x = extent[0] + (extent[2] - extent[0]) / 2;
        var _y = extent[1] + (extent[3] - extent[1]) / 2;
        var center = [_x, _y];
        this.map.getView().animate({
            center: center,
            resolution: resolution,
            duration: 1500
        });
    }

    getFeatureById(layerId, featureId) {
        let layer = this.getLayerById(layerId);

        let idField = this.getLayerIdField(layerId);
        if (layer.length > 0) {
            if (layer[0].isVisible()) {
                let feature = layer[0].getSource().getFeatures().find(x => x.get(idField) == featureId);
                return feature;
            } else {
                swal.fire({ title: 'Atenție!', html: 'Stratul nu este vizibil în hartă.' })
            }
        }

        return null;
    }

    getLayerSettings(layerId) {
        return this.layerSettingsHandler.getLayerSettings(layerId);
    }

    getLayerIdField(layerId) {
        let layerSettings = this.layerSettingsHandler.getLayerSettings(layerId);

        let idField;

        if (layerSettings.Entity != null) {
            if (layerSettings.Entity.Type != 2) {
                idField = layerSettings.Entity.PkFieldName;
            } else {
                idField = layerSettings.Entity.PkField;
            }
        } else if (layerSettings.Geoservice != null) {
            idField = layerSettings.Geoservice.IdColumn;
        } else if (layerSettings.Type == layerTypes.TEMPORARY_VECTOR) {
            idField = layerSettings.idField;
        }

        return idField;
    }

    fitTemporaryFeature(layerId, featureId) {
        let layer = this.getLayerById(layerId);

        if (layer.length > 0) {
            if (layer[0].isVisible()) {
                let feature = layer[0].getSource().getFeatureById(featureId);
                return this._fitFeature(feature, layer[0]);
            } else {
                // TO DO de tradus
                swal.fire({ title: 'Atenție!', html: 'Stratul nu este vizibil în hartă.' })
            }
        }

        return false;
    }

    addTemporaryFeatures(layerId, features, format, clearPrevious, fit) {
        if (format == null) {
            return { status: false, message: 'format is mandatory' };
        }

        if (features == null) {
            return { status: false, message: 'no features were provided' };
        }

        if (layerId == null) {
            return { status: false, message: 'layerId is mandatory' };
        }

        let layer = this.getLayerById(layerId);

        if (layer.length == 0) {
            return { status: false, message: 'layer was not found' };
        }

        let parsedFeatures = this._parseTemporaryFeatures(layer[0], features, format);

        if (clearPrevious) {
            layer[0].getSource().clear();
        }

        if (parsedFeatures.length > 0) {
            layer[0].getSource().addFeatures(parsedFeatures);

            if (fit) {
                this._fitAllLayerFeatures(layer[0]);
            }
        }
    }

    removeTemporaryFeatures(layerId, features) {
        if (layerId == null) {
            return { status: false, message: 'layerId is mandatory' };
        }

        let layer = this.getLayerById(layerId);

        if (layer.length == 0) {
            return { status: false, message: 'layer was not found' };
        }

        let source = layer[0].getSource();

        if (features != null && features.length > 0) {
            features.forEach(feature => {
                let toRemove = source.getFeatureById(feature);

                if (toRemove != null) {
                    source.removeFeatures([toRemove]);
                }
            });
        }
    }

    clearTemporaryFeatures(layerId) {
        if (layerId == null) {
            return { status: false, message: 'layerId is mandatory' };
        }

        let layer = this.getLayerById(layerId);

        if (layer.length == 0) {
            return { status: false, message: 'layer was not found' };
        }

        let source = layer[0].getSource();

        source.clear();
    }

    _registerEventHandlers() {
        $event.register(eventTypes.LAYER_SELECTION_CLEARED, this._getLayerSelectionClearedHandler(), this, this.classId);
        $event.register(eventTypes.FEATURES_MODIFIED, this._getFeaturesModifiedHandler(), this, this.classId);
        $event.register(eventTypes.LAYER_FIT, this._getLayerFitHandler(), this, this.classId);
        $event.register(eventTypes.LEGEND_LAYER_RECREATED, this._getLayerRecreatedHandler(), this, this.classId);
        $event.register(eventTypes.LAYER_ADDED, this._getLayerAddedHandler(), this, this.classId);
        $event.register(eventTypes.CLEAR_TEMPORARY_FEATURES, this._getClearTemporaryFeaturesHandler(), this, this.classId);
    }

    _getFeaturesModifiedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.oldFeatures != null && data.newFeatures != null && data.layerId != null) {
                this._updateLayerFeatures(data.oldFeatures, data.newFeatures, data.layerId);
            }
        };

        return handler;
    }

    _getLayerSelectionClearedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                this.selectionHandler.clearLayerSelection(data.layerId);
            }
        };

        return handler;
    }

    _getLayerFitHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                let layer = this.getLayerById(data.layerId);

                if (layer.length > 0) {
                    if (layer[0].get('temporarySettings') != null) {
                        this._fitAllLayerFeatures(layer[0]);
                    } else {
                        this.layerSettingsHandler.getLayerBbox(data.layerId, this.map.getView().getProjection().getCode()).then(result => {
                            if (result != null && result.trim() != '') {
                                let format = new WKT();
                                let mapProjection = this.generalSettingsHandler.settings.ProjCode;

                                let feature = format.readFeature(result, {
                                    dataProjection: mapProjection,
                                    featureProjection: mapProjection
                                });

                                this._fitFeature(feature);
                            }
                        });
                    }
                }
            }
        };

        return handler;
    }

    _getLayerStyleChangedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                let layer = this.getLayerById(data.layerId);
                this.removeLayers([data.layerId], false);

                if (layer.length > 0) {
                    let zIndex = layer[0].get('zIndex');
                    this._recreateLayer(data.layerId, zIndex);
                }
            }
        };

        return handler;
    }

    changeLayerStyle(layerId) {
        return new Promise((resolve, reject) => {
            let layers = this.getLayerById(layerId);

            if (layers.length > 0) {
                let oldLayer = layers[0];
                let zIndex = oldLayer.get('zIndex');
                let layerSettings = this.layerSettingsHandler.getLayerSettings(oldLayer.get('uid'));
                let layerOptions = this._getVectorLayerOptions(layerSettings, zIndex);

                let source;

                if (oldLayer.getSource != null && oldLayer.getSource() != null && oldLayer.getSource().getSource != null && oldLayer.getSource().getSource() != null) {
                    this.removeLayers([layerId], false);
                    return this._recreateLayer(layerId, zIndex).then(() => { resolve() });
                } else if (oldLayer.getSource != null && oldLayer.getSource() != null) {
                    source = oldLayer.getSource();
                } else {
                    resolve();
                }

                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            let style;

                            if (layerSettings.IdDefaultStyle == null) {
                                style = this.styleHandler.getCombinedStyle('sys_' + styleTypes.SYSTEM_STYLE_ALL, null, null);
                            } else {
                                style = this.styleHandler.getCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
                            }

                            if (style.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                                let styleFunction = (feature) => {
                                    let styleGetter = createOlStyleFunction(style.style, this.map);
                                    return styleGetter(feature);
                                }

                                addOption(layerOptions, "source", source);
                                addOption(layerOptions, "style", styleFunction);
                                //addOption(layerOptions, "imageRatio", 1.5);

                                this.removeLayers([layerId], false);

                                let layer = new VectorImageLayer(layerOptions);
                                this.layers.push(layer);
                                this.map.addLayer(layer);

                                if (layerSettings.IdHighlightStyle != null) {
                                    this.selectionHandler.computeHover(layer);
                                }

                                if (layerSettings.IdSelectStyle != null) {
                                    this.selectionHandler.computeSelection(layer);
                                }

                                resolve(layer);
                            } else if (style.styleType == styleTypes.STYLE_TYPE_HEATMAP) {
                                addOption(layerOptions, "source", source);
                                addOption(layerOptions, "gradient", style.style.ColorRamp);
                                addOption(layerOptions, "radius", style.style.Radius);
                                addOption(layerOptions, "blur", style.style.Blur);

                                if (style.style.IdEntityField != null && style.style.IdEntityField.trim().length > 0) {
                                    addOption(layerOptions, "weight", this._handleWeightFunction(style.style.IdEntityField));
                                }

                                this.removeLayers([layerId], false);

                                let layer = new Heatmap(layerOptions);
                                this.layers.push(layer);
                                this.map.addLayer(layer);

                                if (layerSettings.IdHighlightStyle != null) {
                                    this.selectionHandler.computeHover(layer);
                                }

                                if (layerSettings.IdSelectStyle != null) {
                                    this.selectionHandler.computeSelection(layer);
                                }

                                resolve(layer);
                            } else if (style.styleType == styleTypes.STYLE_TYPE_THEMATIC) {
                                this._handleTheme(layerSettings, style, source).then(themeStyle => {
                                    if (themeStyle != null) {
                                        if (themeStyle.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                                            let styleFunction = (feature) => {
                                                let styleGetter = createOlStyleFunction(themeStyle.style, this.map);
                                                return styleGetter(feature);
                                            }

                                            addOption(layerOptions, "source", source);
                                            addOption(layerOptions, "style", styleFunction);
                                            //addOption(layerOptions, "imageRatio", 1.5);

                                            this.removeLayers([layerId], false);

                                            let layer = new VectorImageLayer(layerOptions);

                                            this.layers.push(layer);
                                            this.map.addLayer(layer);

                                            if (layerSettings.IdHighlightStyle != null) {
                                                this.selectionHandler.computeHover(layer);
                                            }

                                            if (layerSettings.IdSelectStyle != null) {
                                                this.selectionHandler.computeSelection(layer);
                                            }
                                            resolve(layer);
                                        } else {
                                            addOption(layerOptions, "source", source);

                                            this.removeLayers([layerId], false);

                                            addOption(layerOptions, 'style', style.style.featureStyle);
                                            addOption(layerOptions, 'className', 'webgllayer');
                                            let layer = new WebGLLayer(layerOptions);
                                            this.layers.push(layer);
                                            this.map.addLayer(layer);

                                            if (layerSettings.IdHighlightStyle != null) {
                                                this.selectionHandler.computeHover(layer);
                                            }

                                            if (layerSettings.IdSelectStyle != null) {
                                                this.selectionHandler.computeSelection(layer);
                                            }
                                            resolve(layer);
                                        }
                                    } else {
                                        resolve();
                                    }
                                });
                            }
                            else if (style.styleType == 'CUSTOM' && window.CustomStyleHandler) {
                                this.removeLayers([layerId], false);
                                return this._recreateLayer(layerId, zIndex).then(() => { resolve() });
                            }
                            else {
                                addOption(layerOptions, "source", source);
                                this.removeLayers([layerId], false);

                                addOption(layerOptions, 'style', style.style.featureStyle);
                                addOption(layerOptions, 'className', 'webgllayer');
                                let layer = new WebGLLayer(layerOptions);
                                this.layers.push(layer);
                                this.map.addLayer(layer);

                                if (layerSettings.IdHighlightStyle != null) {
                                    this.selectionHandler.computeHover(layer);
                                }

                                if (layerSettings.IdSelectStyle != null) {
                                    this.selectionHandler.computeSelection(layer);
                                }

                                if (style.style.isTimeDependant) {
                                    this._handleLayerTimeDependency(layer.get('uid'));
                                }

                                resolve(layer);
                            }
                        });
                    });
                });
            }
        });
    }

    _handleTheme(layerSettings, style, source) {
        return new Promise((resolve, reject) => {
            let fieldName = style.style.NameEntityField;

            if (style.style.AutoValues == true) {
                if (layerSettings.LoadingStrategy == defaults.WFS_ALL_STRATEGY) {
                    if (source != null && source.getFeatures().length > 0) {
                        let min = Infinity;
                        let max = -Infinity;
                        let features = source.getFeatures();
                        features.forEach(feature => {
                            let value = feature.get(fieldName);

                            if (value != null) {
                                if (value > max) {
                                    max = value;
                                }

                                if (value < min) {
                                    min = value;
                                }
                            }
                        });

                        if (min == Infinity) {
                            min = 0;
                            resolve(null);
                        }

                        if (max == -Infinity) {
                            max = 10000;
                            resolve(null);
                        }

                        let style = this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, min, max);
                        resolve(style);
                    } else {
                        let min = 0;
                        let max = 10000;

                        let style = this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, min, max);
                        resolve(style);
                    }
                } else {
                    if (layerSettings.Entity != null && layerSettings.Entity.Type != 2) {
                        let idEntity = layerSettings.Entity.Id;

                        $.ajax({
                            type: "GET",
                            url: this.rootURL + paths.GET_FIELD_RANGE.replaceAll('{0}', idEntity).replaceAll('{1}', fieldName),
                            async: true,
                            contentType: "application/json; charset=utf-8",
                            success: (result) => {
                                if (result != null) {
                                    let min = result.MinValue;
                                    let max = result.MaxValue;
                                    let style = this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, min, max);
                                    resolve(style);
                                } else {
                                    let min = 0;
                                    let max = 10000;
                                    let style = this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, min, max);
                                    resolve(style);
                                }
                            },
                            error: (e) => {
                                resolve(null);
                            }
                        });
                    } else {
                        let min = 0;
                        let max = 10000;
                        let style = this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, min, max);
                        resolve(style);
                    }
                }
            } else {
                let style = this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, null, null);
                resolve(style);
            }
        });
    }

    _getVectorLayerOptions(layerSettings, idx) {
        switch (layerSettings.Type) {
            case layerTypes.GEOSERVICE: return this._getGeoserviceLayerOptions(layerSettings, idx);
            case layerTypes.ENTITY: return this._getEntityLayerOptions(layerSettings, idx);
            default: return null;
        }
    }

    _getGeoserviceLayerOptions(layerSettings, idx) {
        switch (layerSettings.Geoservice.ServiceType) {
            case layerTypes.WFS: return this._getGeoserviceWfsLayerOptions(layerSettings, idx);
            case layerTypes.GEOJSON: return this._getGeoserviceWfsLayerOptions(layerSettings, idx);
            default: return null;
        }
    }

    _getEntityLayerOptions(layerSettings, idx) {
        switch (layerSettings.Interpretation) {
            case layerTypes.XP_NATIVE: {
                if (layerSettings.Entity.Type == 2) {
                    return this._getAnalysisLayerOptions(layerSettings, idx);
                } else {
                    return this._getNativeLayerOptions(layerSettings, idx);
                }
            }
            case layerTypes.WFS: return this._getWfsLayerOptions(layerSettings, idx, false);
            case layerTypes.VECTOR_TILE: return this._getVectorTileLayerOptions(layerSettings, idx, false);
            default: return null;
        }
    }

    _getGeoserviceWfsLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);

        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Geoservice.TileMatrix));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Geoservice.TileMatrix));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Geoservice.IdColumn);

        return layerOptions;
    }

    _getAnalysisLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);

        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

        return layerOptions;
    }

    _getNativeLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);
        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

        return layerOptions;
    }

    _getVectorTileLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);

        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

        return layerOptions;
    }

    _getWfsLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);

        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

        return layerOptions;
    }

    _getLayerStyleModifiedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                let layer = this.getLayerById(data.layerId);
                this.removeLayers([data.layerId], false);

                if (layer.length > 0) {
                    let zIndex = layer[0].get('zIndex');
                    this._recreateLayer(data.layerId, zIndex);
                }
            }
        };

        return handler;
    }

    _getClearTemporaryFeaturesHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                this.clearTemporaryFeatures(data.layerId);
            }
        };

        return handler;
    }

    _getLayerAddedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                this.layerIdx++;
                showLoadingPanel();
                this._recreateLayer(data.layerId, this.layerIdx).then(() => {
                    this.changeLayersOrder(this.layerSettingsHandler.layers, true);
                    hideLoadingPanel();
                });
            }
        };

        return handler;
    }

    _getLayerRecreatedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                let layer = this.getLayerById(data.layerId);
                this.removeLayers([data.layerId], false);

                if (layer.length > 0) {
                    let zIndex = layer[0].get('zIndex');
                    this._recreateLayer(data.layerId, zIndex);
                }
            }
        };

        return handler;
    }

    _recreateLayer(layer, zIndex) {
        if (this.refreshIds[layer]) {
            clearInterval(this.refreshIds[layer]);
            delete this.refreshIds[layer];
        }
        let layerSetting = this.layerSettingsHandler.getLayerSettings(layer);
        return this._createLayer(layerSetting, zIndex).then(() => { });
    }

    _updateLayerFeatures(oldFeatures, newFeatures, layerId) {
        newFeatures.forEach(newFeature => {
            newFeature.layerId = layerId;
            newFeature.set('xstate', styleTypes.DEFAULT_STYLE_TYPE);
        });

        let layer = this.getLayerById(layerId);

        if (layer.length > 0) {
            layer[0].getSource().removeFeatures(oldFeatures);
            layer[0].getSource().addFeatures(newFeatures);

            if (layer[0].get('temporarySettings') != null && layer[0].get('temporarySettings').featureEditCallback != null) {
                let layerSettings = layer[0].get('temporarySettings')
                let format = new WKT();
                let mapProjection = this.generalSettingsHandler.settings.ProjCode;

                let result = [];

                newFeatures.forEach(newFeature => {
                    var feature = format.writeFeature(newFeature, {
                        dataProjection: layerSettings.srid,
                        featureProjection: mapProjection
                    });

                    result.push(feature);
                });

                layer[0].get('temporarySettings').featureEditCallback(result, layerSettings.uid);
            }
        }
    }

    _fitFeature(feature, layer) {
        if (feature == null) {
            return false;
        }

        let minScale = 500;

        if (layer!= null && layer.get('temporarySettings') != null) {
            minScale = layer.get('temporarySettings').minFitScale;
        }

        let minResolution = getResolutionForScale(minScale, this.generalSettingsHandler.settings.ProjCode);

        let extent = feature.getGeometry().getExtent();

        var resolutionForExtent = this.map.getView().getResolutionForExtent(extent);
        var resolution = Math.max(resolutionForExtent, minResolution);

        var _x = extent[0] + (extent[2] - extent[0]) / 2;
        var _y = extent[1] + (extent[3] - extent[1]) / 2;
        var center = [_x, _y];
        this.map.getView().animate({
            center: center,
            resolution: resolution,
            duration: 1500
        });

        return true;
    }

    _fitAllLayerFeatures(layer) {
        if (layer.getSource().getFeatures().length == 0) {
            return;
        }
        let minScale = 500;

        if (layer.get('temporarySettings') != null) {
            minScale = layer.get('temporarySettings').minFitScale;
        }

        let minResolution = getResolutionForScale(minScale, this.generalSettingsHandler.settings.ProjCode);

        let extent = layer.getSource().getExtent();

        var resolutionForExtent = this.map.getView().getResolutionForExtent(extent);
        var resolution = Math.max(resolutionForExtent, minResolution);

        var _x = extent[0] + (extent[2] - extent[0]) / 2;
        var _y = extent[1] + (extent[3] - extent[1]) / 2;
        var center = [_x, _y];
        this.map.getView().animate({
            center: center,
            resolution: resolution,
            duration: 1500
        });
    }

    _parseTemporaryFeatures(layer, features, format) {
        let layerSettings = layer.get('temporarySettings');

        switch (format) {
            case formats.WKT: return this._parseWktFeatures(features, layerSettings.geomField, layerSettings.idField, layerSettings.srid, layerSettings.name);
            default: return [];
        }
    }

    _parseWktFeatures(features, geomField, idField, srid, layerId) {
        let format = new WKT();
        let mapProjection = this.generalSettingsHandler.settings.ProjCode;
        let result = [];

        for (let i = 0; i < features.length; i++) {
            if (features[i][geomField] != null && features[i][geomField].trim() != '') {
                var feature = format.readFeature(features[i][geomField], {
                    dataProjection: srid,
                    featureProjection: mapProjection
                });

                features[i].xstate = styleTypes.DEFAULT_STYLE_TYPE;
                feature.setId(features[i][idField]);
                feature.setProperties(features[i]);
                feature.layerId = layerId;
                result.push(feature);
            }
        }

        return result;
    }

    _createLayers(layers, layerPromises) {
        let mapFilters = localStorage.getItem('mapFilters');

        if (mapFilters == null) {
            mapFilters = null;
        } else {
            mapFilters = JSON.parse(mapFilters);
        }

        for (let i = layers.length - 1; i >= 0; i--) {
            let layer = layers[i];

            if (mapFilters != null && mapFilters[layer.Id] != null) {
                layer.Filtered = true;
                layer.Filters = mapFilters[layer.Id];
                layer.WfsFilters = mapFilters[layer.Id + '_wfs'];
            }

            if (layer.Type == layerTypes.GROUP) {
                this._createLayers(layer.Items, layerPromises);
            } else {
                layerPromises.push(this._createLayer(layer, this.layerIdx));
                this.layerIdx++;
            }
        }
    }

    _createLayer(layer, idx) {
        switch (layer.Type) {
            case layerTypes.GEOSERVICE: return this._createGeoserviceLayer(layer, idx);
            case layerTypes.ENTITY: return this._handleEntityLayer(layer, idx);
            default: return new Promise((resolve, reject) => { resolve(); });
        }
    }

    _handleEntityLayer(layer, idx) {
        if (layer.url != null) {
            return this._createNativeLayer(layer, idx);
        }

        switch (layer.Interpretation) {
            case layerTypes.XP_NATIVE: {
                if (layer.Entity.Type == 2) {
                    return this._createAnalysisLayer(layer, idx);
                } else {
                    return this._createNativeLayer(layer, idx);
                }
            }
            case layerTypes.WFS: return this._createWfsLayer(layer, idx, false);
            case layerTypes.VECTOR_TILE: return this._createVectorTileLayer(layer, idx, false);
            default: return new Promise((resolve, reject) => { resolve(); });
        }
    }

    _createAnalysisLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            let srid = layerSettings.Entity.OutputValues.find(x => { return x.OutputName == layerSettings.Entity.GeomField }).Srid;
            this.projectionHandler.load(srid).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            let loadingStrategy = layerSettings.LoadingStrategy == null || layerSettings.LoadingStrategy == '' ? defaults.WFS_DEFAULT_LOADING_STRATEGY : layerSettings.LoadingStrategy;

                            switch (loadingStrategy) {
                                case 'bboxStrategy':
                                    addOption(sourceOptions, 'strategy', bboxStrategy);
                                    break;
                                case "allStrategy":
                                    addOption(sourceOptions, 'strategy', allStrategy);
                                    break;
                            }

                            var source = new VectorSource(sourceOptions);
                            source.setLoader(handleAnalysisLoader(layerSettings, this.map, this.rootURL, source, this.selectionHandler, this.mapControl.generalActionHandler));

                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, srid));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, srid));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Entity.PkField);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then((created) => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createVectorTileLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Entity.GeometrySrid).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var projection = getProjection(this.generalSettingsHandler.settings.ProjCode);
                            addOption(sourceOptions, 'tileGrid', this._getTileGrid(this.generalSettingsHandler.settings.ProjCode));
                            addOption(sourceOptions, 'url', handleGeoserverVectorTileLayerUrl(this.mapId, this.rootURL, layerSettings, this.map, this.gisSettingsHandler));
                            addOption(sourceOptions, 'projection', projection);

                            var format = layerSettings.Format == null ? this.gisSettingsHandler.settings.VectorTile_Format : layerSettings.Format;

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Entity.GeometryFieldName != null && layerSettings.Entity.GeometryFieldName != '') {
                                formatOptions.geometryName = layerSettings.Entity.GeometryFieldName;
                            }

                            formatOptions.dataProjection = projection;
                            formatOptions.featureProjection = projection;

                            switch (format) {
                                case 'application/vnd.mapbox-vector-tile':
                                    addOption(sourceOptions, 'format', new XMVT(formatOptions));
                                    break;
                                case 'application/json;type=geojson':
                                    addOption(sourceOptions, 'format', new XGeoJSON(formatOptions));
                                    break;
                                case 'application/json;type=topojson':
                                    addOption(sourceOptions, 'format', new XTopoJSON(formatOptions));
                                    break;
                                default:
                                    addOption(sourceOptions, 'format', new XMVT(formatOptions));
                                    break;
                            }

                            var source = new VectorTileSource(sourceOptions);

                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

                            let style;

                            if (layerSettings.IdDefaultStyle == null) {
                                style = this.styleHandler.getCombinedStyle('sys_' + styleTypes.SYSTEM_STYLE_ALL, null, null);
                            } else {
                                style = this.styleHandler.getCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
                            }

                            let styleFunction = (feature) => {
                                let styleGetter = createOlStyleFunction(style.style, this.map);
                                return styleGetter(feature);
                            }

                            addOption(layerOptions, "style", styleFunction);

                            let layer = new VectorTileLayer(layerOptions);
                            this.layers.push(layer);
                            this.map.addLayer(layer);

                            if (layerSettings.IdHighlightStyle != null) {
                                this.selectionHandler.computeHover(layer);
                            }

                            if (layerSettings.IdSelectStyle != null) {
                                this.selectionHandler.computeSelection(layer);
                            }

                            resolve(layer);
                        });
                    });
                });
            });
        });
    }

    _getTileGrid(projId) {
        var projSettings = this.projectionHandler.projections[projId];

        if (projSettings == null || projSettings.GridMatrixLevels == null || projSettings.GridMatrixLevels == 0) {
            return null;
        }

        var fullProjection = getProjection(projId);
        var projectionExtent = fullProjection.getExtent();
        var size = getHeight(projectionExtent) / this.gisSettingsHandler.settings.TileSize;
        var resolutions = new Array(projSettings.GridMatrixLevels);
        for (var z = 0; z < projSettings.GridMatrixLevels; z++) {
            resolutions[z] = size / Math.pow(2, z);
        }

        var tileOptions = {};
        addOption(tileOptions, "extent", projectionExtent);
        addOption(tileOptions, "origin", getTopLeft(projectionExtent));
        addOption(tileOptions, "resolutions", resolutions);
        addOption(tileOptions, "tileSize", [this.gisSettingsHandler.settings.TileSize, this.gisSettingsHandler.settings.TileSize]);

        return new TileGrid(tileOptions);
    }

    _createWfsLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Entity.GeometrySrid).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Entity.GeometryFieldName != null && layerSettings.Entity.GeometryFieldName != '') {
                                formatOptions.geometryName = layerSettings.Entity.GeometryFieldName;
                            }

                            var geoJsonFormat = new XGeoJSON(formatOptions);

                            addOption(sourceOptions, 'format', geoJsonFormat);
                            addOption(sourceOptions, 'url', handleWfsUrl(this.mapId, this.rootURL, layerSettings, this.map, this.gisSettingsHandler));

                            addOption(sourceOptions, 'loader', xhr(sourceOptions['url'], sourceOptions['format']));

                            let loadingStrategy = layerSettings.LoadingStrategy == null || layerSettings.LoadingStrategy == '' ? defaults.WFS_DEFAULT_LOADING_STRATEGY : layerSettings.LoadingStrategy;

                            switch (loadingStrategy) {
                                case 'bboxStrategy':
                                    addOption(sourceOptions, 'strategy', bboxStrategy);
                                    break;
                                case "allStrategy":
                                    addOption(sourceOptions, 'strategy', allStrategy);
                                    break;
                            }
                            var source = new VectorSource(sourceOptions);


                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then(() => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createNativeLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Entity.GeometrySrid).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Entity.GeometryFieldName != null && layerSettings.Entity.GeometryFieldName != '') {
                                formatOptions.geometryName = layerSettings.Entity.GeometryFieldName;
                            }

                            var geoJsonFormat = new XGeoJSON(formatOptions);

                            addOption(sourceOptions, 'format', geoJsonFormat);
                            addOption(sourceOptions, 'url', handleNativeUrl(this.mapId, layerSettings, this.map, this.rootURL));

                            addOption(sourceOptions, 'loader', xhr(sourceOptions['url'], sourceOptions['format']));

                            let loadingStrategy = layerSettings.LoadingStrategy == null || layerSettings.LoadingStrategy == '' ? defaults.WFS_DEFAULT_LOADING_STRATEGY : layerSettings.LoadingStrategy;

                            switch (loadingStrategy) {
                                case 'bboxStrategy':
                                    addOption(sourceOptions, 'strategy', bboxStrategy);
                                    break;
                                case "allStrategy":
                                    addOption(sourceOptions, 'strategy', allStrategy);
                                    break;
                            }
                            var source = new VectorSource(sourceOptions);


                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then(() => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createVectorLayer(layerSettings, layerOptions, sourceOptions) {
        return new Promise((resolve, reject) => {
            let style;

            if (layerSettings.IdDefaultStyle == null) {
                style = this.styleHandler.getCombinedStyle('sys_' + styleTypes.SYSTEM_STYLE_ALL, null, null);
            } else {
                style = this.styleHandler.getCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
            }

            let layer;

            if (layerSettings.RefreshTime != null && !isNaN(layerSettings.RefreshTime) && parseInt(layerSettings.RefreshTime.toString()) > 0) {

                const layerId = layerSettings.Id;
                const time = parseInt(layerSettings.RefreshTime.toString()) * 1000;
                let intervalId = setInterval(() => {
                    this.refreshLayer(layerId, true);
                }, time);

                this.refreshIds[layerId] = intervalId;
            }

            if (style.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                let styleFunction = (feature) => {
                    let styleGetter = createOlStyleFunction(style.style, this.map);
                    return styleGetter(feature);
                }

                addOption(layerOptions, "style", styleFunction);
                //addOption(layerOptions, "imageRatio", 1.5);

                layer = new VectorImageLayer(layerOptions);
                this.layers.push(layer);
                this.map.addLayer(layer);

                if (layerSettings.IdHighlightStyle != null) {
                    this.selectionHandler.computeHover(layer);
                }

                if (layerSettings.IdSelectStyle != null) {
                    this.selectionHandler.computeSelection(layer);
                }

                resolve(layer);
            } else if (style.styleType == styleTypes.STYLE_TYPE_HEATMAP) {
                addOption(layerOptions, "gradient", style.style.ColorRamp);
                addOption(layerOptions, "radius", style.style.Radius);
                addOption(layerOptions, "blur", style.style.Blur);

                if (style.style.IdEntityField != null && style.style.IdEntityField.trim().length > 0) {
                    addOption(layerOptions, "weight", this._handleWeightFunction(style.style.IdEntityField));
                }

                layer = new Heatmap(layerOptions);
                this.layers.push(layer);
                this.map.addLayer(layer);

                if (layerSettings.IdHighlightStyle != null) {
                    this.selectionHandler.computeHover(layer);
                }

                if (layerSettings.IdSelectStyle != null) {
                    this.selectionHandler.computeSelection(layer);
                }

                resolve(layer);
            } else if (style.styleType == styleTypes.STYLE_TYPE_THEMATIC) {
                let idEntity = layerSettings.Entity.Id;
                let fieldName = style.style.NameEntityField;

                if (style.style.AutoValues == true) {
                    $.ajax({
                        type: "GET",
                        url: this.rootURL + paths.GET_FIELD_RANGE.replaceAll('{0}', idEntity).replaceAll('{1}', fieldName),
                        async: true,
                        contentType: "application/json; charset=utf-8",
                        success: (result) => {
                            if (result != null) {
                                let min = result.MinValue;
                                let max = result.MaxValue;

                                style = this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, min, max);

                                if (style.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                                    let styleFunction = (feature) => {
                                        let styleGetter = createOlStyleFunction(style.style, this.map);
                                        return styleGetter(feature);
                                    }

                                    addOption(layerOptions, "style", styleFunction);

                                    //addOption(layerOptions, "imageRatio", 1.5);
                                    layer = new VectorImageLayer(layerOptions);
                                    this.layers.push(layer);
                                    this.map.addLayer(layer);

                                    if (layerSettings.IdHighlightStyle != null) {
                                        this.selectionHandler.computeHover(layer);
                                    }

                                    if (layerSettings.IdSelectStyle != null) {
                                        this.selectionHandler.computeSelection(layer);
                                    }
                                } else {
                                    addOption(layerOptions, 'style', style.style.featureStyle);
                                    addOption(layerOptions, 'className', 'webgllayer');
                                    layer = new WebGLLayer(layerOptions);
                                    this.layers.push(layer);
                                    this.map.addLayer(layer);

                                    if (layerSettings.IdHighlightStyle != null) {
                                        this.selectionHandler.computeHover(layer);
                                    }

                                    if (layerSettings.IdSelectStyle != null) {
                                        this.selectionHandler.computeSelection(layer);
                                    }
                                }
                                resolve(layer);
                            } else {
                                resolve();
                            }
                        },
                        error: (e) => {
                            resolve();
                        }
                    });
                } else {
                }
            }
            else if (style.styleType == 'CUSTOM' && window.CustomStyleHandler) {
                if (this.customStyleHandler == null) {
                    this.customStyleHandler = new CustomStyleHandler(this.styleHandler);
                }

                this.customStyleHandler.handleCustomStyleLayer(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, layerSettings, layerOptions, sourceOptions, this.map, this.layers, this.projectionHandler).then(() => {
                    resolve(layer);
                });
            }
            else {
                addOption(layerOptions, 'style', style.style.featureStyle);
                addOption(layerOptions, 'className', 'webgllayer');
                layer = new WebGLLayer(layerOptions);
                this.layers.push(layer);
                this.map.addLayer(layer);

                if (layerSettings.IdHighlightStyle != null) {
                    this.selectionHandler.computeHover(layer);
                }

                if (layerSettings.IdSelectStyle != null) {
                    this.selectionHandler.computeSelection(layer);
                }

                if (style.style.isTimeDependant) {
                    this._handleLayerTimeDependency(layer.get('uid'));
                }

                resolve(layer);
            }
        });
    }

    _handleLayerTimeDependency(layerId) {
        let layers = this.getLayerById(layerId);
        if (layers.length > 0) {
            let layer = layers[0];

            let features = layer.getSource().getFeatures();
            let currentTime = Math.floor(new Date().getTime() / 1000);
            if (features.length > 0) {
                features.forEach((feature) => {
                    feature.set('currentTime', currentTime);
                });
            }
        }
        return requestAnimationFrame(() => { this._handleLayerTimeDependency(layerId) });
    }

    _createGeoserviceLayer(layer, idx) {
        switch (layer.Geoservice.ServiceType) {
            case layerTypes.OSM: return this._createOsmLayer(layer, idx);
            case layerTypes.WMS: return this._createGeoserviceWmsLayer(layer, idx);
            case layerTypes.WFS: return this._createGeoserviceWfsLayer(layer, idx);
            case layerTypes.GEOJSON: return this._createGeoserviceGeojsonLayer(layer, idx);
            case layerTypes.URL: return this._createGeoserviceUrlLayer(layer, idx);
            case layerTypes.WMTS: return this._createGeoserviceWmtsLayer(layer, idx);
            default: return new Promise((resolve, reject) => { resolve(); });
        }
    }

    _createGeoserviceWmtsLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            let url = layerSettings.Geoservice.Url;

            if (url.indexOf('http') != 0) {
                url = this.rootURL
                    + paths.PROXY_URL
                    + this.mapId
                    + '/'
                    + layerSettings.Fid.replaceAll('-', '')
                    + layerSettings.Geoservice.Url;
            }

            fetch(url, { mode: 'cors', credentials: 'omit' })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`WMTS GetCapabilities failed: ${response.status} ${response.statusText}`);
                    }
                    return response.text();
                })
                .then(responseText => {
                    if (!responseText) {
                        // no capabilities returned
                        resolve(null);
                        return;
                    }

                    const result = (new WMTSCapabilities()).read(responseText);
                    const options = optionsFromCapabilities(result, {
                        layer: layerSettings.Geoservice.LayerName,
                        matrixSet: layerSettings.Geoservice.TileMatrix,
                        format: layerSettings.Geoservice.Format,
                        crossOrigin: 'anonymous',
                        transition: 0,
                        tilePixelRatio: 1
                    });

                    const source = new WMTS(options);
                    this.tileSources.push(source);

                    const layerOptions = {};
                    addOption(layerOptions, "uid", layerSettings.Id);
                    addOption(layerOptions, "source", source);
                    addOption(layerOptions, "visible", layerSettings.VisibleByDefault);
                    addOption(layerOptions, "preload", Infinity);
                    addOption(layerOptions, "zIndex", idx);
                    addOption(layerOptions, "originalZIndex", idx);
                    addOption(layerOptions, "extent", this._getLayerExtent(layerSettings));
                    addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, defaults.DEFAULT_SRID));
                    addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, defaults.DEFAULT_SRID));
                    addOption(layerOptions, "interpretation", layerTypes.WMTS);
                    addOption(layerOptions, "loadTilesWhileAnimating", true);
                    addOption(layerOptions, "loadTilesWhileInteracting", true);

                    const layer = new TileLayer(layerOptions);
                    this.layers.push(layer);
                    this.map.addLayer(layer);

                    resolve();
                })
                .catch(err => {
                    console.error('Error loading WMTS capabilities:', err);
                    resolve();
                });
        });
    }

    _createOsmLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            var layerOptions = {};
            addOption(layerOptions, 'uid', layerSettings.Id);

            let source = new OSM({
                transition: 0,
                tilePixelRatio: 1
            });

            this.tileSources.push(source);

            addOption(layerOptions, 'source', source);
            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
            addOption(layerOptions, 'preload', Infinity);
            addOption(layerOptions, 'zIndex', idx);
            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, defaults.DEFAULT_SRID));
            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, defaults.DEFAULT_SRID));
            addOption(layerOptions, 'interpretation', layerTypes.WMTS);
            addOption(layerOptions, "loadTilesWhileAnimating", true);
            addOption(layerOptions, "loadTilesWhileInteracting", true);
            let layer = new TileLayer(layerOptions)
            this.layers.push(layer);
            this.map.addLayer(layer);
            resolve();
        });
    }

    _createGeoserviceUrlLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            var layerOptions = {};
            addOption(layerOptions, 'uid', layerSettings.Id);

            let url = layerSettings.Geoservice.Url;

            if (url.indexOf('http') != 0) {
                url = this.rootURL + paths.PROXY_URL + this.mapId + '/' + layerSettings.Fid.replaceAll('-', '') + layerSettings.Geoservice.Url;
            }

            let source = new ImageTile({
                url: url,
                transition: 0,
                tilePixelRatio: 1
            });

            this.tileSources.push(source);

            addOption(layerOptions, 'source', source);
            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
            addOption(layerOptions, 'preload', Infinity);
            addOption(layerOptions, 'zIndex', idx);
            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, defaults.DEFAULT_SRID));
            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, defaults.DEFAULT_SRID));
            addOption(layerOptions, 'interpretation', layerTypes.WMTS);
            addOption(layerOptions, "loadTilesWhileAnimating", true);
            addOption(layerOptions, "loadTilesWhileInteracting", true);
            let layer = new TileLayer(layerOptions)
            this.layers.push(layer);
            this.map.addLayer(layer);
            resolve();
        });
    }

    _createGeoserviceWmsLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Geoservice.TileMatrix).then(() => {
                var sourceOptions = {};
                var params = {};

                let url = layerSettings.Geoservice.RequestUrl.replaceAll('SERVICE=WMS&', '');

                if (url.indexOf('http') != 0) {
                    url = this.rootURL + paths.PROXY_URL + this.mapId + '/' + layerSettings.Fid.replaceAll('-', '') + layerSettings.Geoservice.RequestUrl.replaceAll('SERVICE=WMS&', '');
                }

                addOption(params, 'LAYERS', layerSettings.Geoservice.LayerName);
                addOption(params, 'FORMAT', layerSettings.Geoservice.Format == null ? formats.DEFAULT_WMS_FORMAT : layerSettings.Geoservice.Format);
                addOption(params, 'VERSION', layerSettings.Geoservice.Version);
                addOption(sourceOptions, 'params', params);
                addOption(sourceOptions, 'url', url);
                addOption(sourceOptions, 'projection', this.projectionHandler.getProj(layerSettings.Geoservice.TileMatrix));
                addOption(sourceOptions, 'ratio', defaults.WMS_RATIO);
                addOption(sourceOptions, 'crossOrigin', defaults.CROSS_ORIGIN);

                var source = new ImageWMS(sourceOptions);

                var layerOptions = {};
                addOption(layerOptions, 'uid', layerSettings.Id);
                addOption(layerOptions, 'source', source);
                addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Geoservice.TileMatrix));
                addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Geoservice.TileMatrix));
                addOption(layerOptions, 'zIndex', idx);
                addOption(layerOptions, "interpretation", layerTypes.WMS);

                let layer = new ImageLayer(layerOptions);
                this.layers.push(layer);
                this.map.addLayer(layer);

                resolve();
            });
        });
    }

    _createGeoserviceGeojsonLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Geoservice.TileMatrix).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Geoservice.GeometryColumn != null && layerSettings.Geoservice.GeometryColumn != '') {
                                formatOptions.geometryName = layerSettings.Geoservice.GeometryColumn;
                            }

                            var geoJsonFormat = new XGeoJSON(formatOptions);

                            addOption(sourceOptions, 'format', geoJsonFormat);
                            addOption(sourceOptions, 'url', handleGeoserviceGeojsonWfsUrl(this.mapId, this.rootURL, layerSettings, this.map));

                            addOption(sourceOptions, 'loader', xhr(sourceOptions['url'], sourceOptions['format']));

                            addOption(sourceOptions, 'strategy', allStrategy);
                            var source = new VectorSource(sourceOptions);

                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Geoservice.TileMatrix));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Geoservice.TileMatrix));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Geoservice.IdColumn);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then(() => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createGeoserviceWfsLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Geoservice.TileMatrix).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Geoservice.GeometryColumn != null && layerSettings.Geoservice.GeometryColumn != '') {
                                formatOptions.geometryName = layerSettings.Geoservice.GeometryColumn;
                            }

                            var geoJsonFormat = new XGeoJSON(formatOptions);

                            addOption(sourceOptions, 'format', geoJsonFormat);
                            addOption(sourceOptions, 'url', handleGeoserviceWfsUrl(this.mapId, this.rootURL, layerSettings, this.map));

                            addOption(sourceOptions, 'loader', xhr(sourceOptions['url'], sourceOptions['format']));

                            let loadingStrategy = layerSettings.LoadingStrategy == null || layerSettings.LoadingStrategy == '' ? defaults.WFS_DEFAULT_LOADING_STRATEGY : layerSettings.LoadingStrategy;

                            switch (loadingStrategy) {
                                case 'bboxStrategy':
                                    addOption(sourceOptions, 'strategy', bboxStrategy);
                                    break;
                                case "allStrategy":
                                    addOption(sourceOptions, 'strategy', allStrategy);
                                    break;
                            }
                            var source = new VectorSource(sourceOptions);


                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Geoservice.TileMatrix));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Geoservice.TileMatrix));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Geoservice.IdColumn);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then(() => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createTemporaryLayer(options) {
        if (options.parentId != null && this.layerSettingsHandler.getLayerSettings(options.parentId) == null && options.addToLegend) {
            this.layerSettingsHandler.addLayer({
                Type: layerTypes.GROUP,
                Id: options.parentId,
                Title: options.parentDisplayName,
                VisibleByDefault: options.visible != null ? options.visible : true,
                Items: []
            }, 0, null);
        }

        return new Promise((resolve, reject) => {
            this.projectionHandler.load(options.srid).then(() => {
                let zIndex = this.temporaryLayerIdx;

                if (options.addToLegend) {
                    if (options.zIndex != null) {
                        zIndex = options.zIndex;
                    } else {
                        zIndex = this.layerIdx;
                        this.layerIdx++;
                    }
                } else {
                    this.temporaryLayerIdx++;
                }

                let sourceOptions = {};
                addOption(sourceOptions, 'strategy', allStrategy);

                let source = new VectorSource(sourceOptions);

                let layerOptions = {};

                addOption(layerOptions, "uid", options.name);
                addOption(layerOptions, "source", source);

                addOption(layerOptions, 'visible', options.visible != null ? options.visible : true);
                addOption(layerOptions, "declutter", options.declutter != null ? options.declutter : true);
                addOption(layerOptions, "zIndex", zIndex);
                addOption(layerOptions, "interpretation", layerTypes.WFS);
                addOption(layerOptions, 'idField', options.idField);
                addOption(layerOptions, 'temporarySettings', options);

                if (options.ignoreStyle) {
                    let layer = new VectorImageLayer(layerOptions);
                    this.layers.push(layer);
                    this.map.addLayer(layer);

                    if (options.addToLegend) {
                        this.layerSettingsHandler.addLayer(options, 0, options.parentId, options.showLegend != null ? !options.showLegend : false);
                    }

                    if (options.selectable) {
                        this.selectionHandler.computeHover(layer, {
                            Temporary: {
                                IdColumn: options.idField,
                                Tooltip: options.Tooltip
                            },
                            Selectable: options.Selectable
                        });

                        this.selectionHandler.computeSelection(layer, {
                            Temporary: {
                                IdColumn: options.idField,
                                Tooltip: options.Tooltip
                            },
                            Selectable: options.Selectable
                        });
                    }

                    resolve({
                        status: true
                    });
                } else {
                    this.styleHandler.loadStyle(options.style, options.isSystemStyle).then(() => {
                        this.styleHandler.loadStyle(options.highlightStyle, options.isSystemHighlightStyle).then(() => {
                            this.styleHandler.loadStyle(options.selectStyle, options.isSystemSelectStyle).then(() => {
                                let style = this.styleHandler.getCombinedStyle(options.style, options.highlightStyle, options.selectStyle);

                                let layer;

                                if (style.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                                    let styleFunction = (feature) => {
                                        let styleGetter = createOlStyleFunction(style.style, this.map);
                                        return styleGetter(feature);
                                    }

                                    addOption(layerOptions, "style", styleFunction);

                                    layer = new VectorImageLayer(layerOptions);
                                    this.layers.push(layer);
                                    this.map.addLayer(layer);
                                } else if (style.styleType == styleTypes.STYLE_TYPE_HEATMAP) {
                                    addOption(layerOptions, "gradient", style.style.ColorRamp);
                                    addOption(layerOptions, "radius", style.style.Radius);
                                    addOption(layerOptions, "blur", style.style.Blur);

                                    if (style.style.IdEntityField != null && style.style.IdEntityField.trim().length > 0) {
                                        addOption(layerOptions, "weight", this._handleWeightFunction(style.style.IdEntityField));
                                    }

                                    layer = new Heatmap(layerOptions);
                                    this.layers.push(layer);
                                    this.map.addLayer(layer);
                                } else {
                                    addOption(layerOptions, 'style', style.style.featureStyle);
                                    addOption(layerOptions, 'className', 'webgllayer');
                                    layer = new WebGLLayer(layerOptions);
                                    this.layers.push(layer);
                                    this.map.addLayer(layer);
                                }

                                if (options.highlightStyle != null) {
                                    this.selectionHandler.computeHover(layer, {
                                        Temporary: {
                                            IdColumn: options.idField,
                                            Tooltip: options.Tooltip
                                        },
                                        Selectable: options.Selectable
                                    });
                                }

                                if (options.selectStyle != null) {
                                    this.selectionHandler.computeSelection(layer, {
                                        Temporary: {
                                            IdColumn: options.idField,
                                            Tooltip: options.Tooltip
                                        },
                                        Selectable: options.Selectable
                                    });
                                }

                                if (options.addToLegend) {
                                    this.layerSettingsHandler.addLayer(options, 0, options.parentId, options.showLegend != null ? !options.showLegend : false);
                                }

                                resolve({
                                    status: true
                                });
                            });
                        });
                    });
                }
            });
        });
    }

    _handleWeightFunction(weight) {
        var weightFunction = function (feature) {
            let weightValue = feature.get(weight);
            if (weightValue != null) {
                return weightValue;
            } else {
                return 0;
            }
        }

        return weightFunction;
    }

    _checkTemporaryVector(options) {
        let result = {
            message: null,
            status: true
        };

        if (options.name == null) {
            result.status = false;
            result.message = 'name is mandatory';
            return result;
        }

        if (this.getLayerById(options.name).length > 0) {
            result.status = false;
            result.message = 'Layer already exists';
            return result;
        }

        if (options.geometryType == null || options.geometryType.trim() == '') {
            result.status = false;
            result.message = 'geometryType is mandatory';
            return result;
        }

        if (options.srid == null || options.srid.trim() == '') {
            result.status = false;
            result.message = 'srid is mandatory';
            return result;
        }

        if (options.geomField == null || options.geomField.trim() == '') {
            result.status = false;
            result.message = 'geomField is mandatory';
            return result;
        }

        if (options.idField == null || options.idField.trim() == '') {
            result.status = false;
            result.message = 'idField is mandatory';
            return result;
        }

        if (options.style == null || options.style.trim() == '') {
            options.isSystemStyle = true;
            switch (options.geometryType) {
                case geomTypes.GEOM_POINT: options.style = 'sys_' + styleTypes.SYSTEM_STYLE_POINT;
                    break;
                case geomTypes.GEOM_LINE: options.style = 'sys_' + styleTypes.SYSTEM_STYLE_LINE;
                    break;
                case geomTypes.GEOM_POLYGON: options.style = 'sys_' + styleTypes.SYSTEM_STYLE_POLYGON;
                    break;
                default: options.style = 'sys_' + styleTypes.SYSTEM_STYLE_ALL;
                    break;
            }
        }

        if ((options.highlightStyle == null || options.highlightStyle.trim() == '') && options.isSystemStyle) {
            options.isSystemHighlightStyle = true;
            switch (options.geometryType) {
                case geomTypes.GEOM_POINT: options.highlightStyle = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_POINT;
                    break;
                case geomTypes.GEOM_LINE: options.highlightStyle = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_LINE;
                    break;
                case geomTypes.GEOM_POLYGON: options.highlightStyle = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_POLYGON;
                    break;
                default: options.highlightStyle = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_ALL;
                    break;
            }
        }

        if ((options.selectStyle == null || options.selectStyle.trim() == '') && options.isSystemStyle) {
            options.isSystemSelectStyle = true;
            switch (options.geometryType) {
                case geomTypes.GEOM_POINT: options.selectStyle = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_POINT;
                    break;
                case geomTypes.GEOM_LINE: options.selectStyle = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_LINE;
                    break;
                case geomTypes.GEOM_POLYGON: options.selectStyle = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_POLYGON;
                    break;
                default: options.selectStyle = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_ALL;
                    break;
            }
        }

        if (options.minFitScale == null) {
            switch (options.geometryType) {
                case geomTypes.GEOM_POINT: options.minFitScale = 1500;
                    break;
                case geomTypes.GEOM_LINE: options.minFitScale = 2500;
                    break;
                case geomTypes.GEOM_POLYGON: options.minFitScale = 5000;
                    break;
                default: options.minFitScale = 5000;
                    break;
            }
        }

        options.Type = layerTypes.TEMPORARY_VECTOR;
        options.VisibleByDefault = options.visible != null ? options.visible : true;
        options.Id = options.name;
        options.Opacity = 1.0;
        options.Title = options.displayName;
        options.IdDefaultStyle = options.style;
        options.Interpretation = layerTypes.WFS;

        if (options.highlightStyle != null) {
            options.IdHighlightStyle = options.highlightStyle;
            options.Selectable = true;
        }

        if (options.selectStyle != null) {
            options.IdSelectStyle = options.selectStyle;
            options.Selectable = true;
        }

        if (options.selectable) {
            options.Selectable = true;
        }

        options.Tooltip = options.tooltip;

        return result;
    }

    _getLayerExtent(layer) {
        let xMin = layer.XMin;
        let yMin = layer.YMin;
        let xMax = layer.XMax;
        let yMax = layer.YMax;

        if (xMin != null && yMin != null && xMax != null && yMax != null) {
            return [xMin, yMin, xMax, yMax];
        }

        return this.projectionHandler.getProjExtent(this.generalSettingsHandler.settings.ProjCode);
    }
}